import { SafeHydrate } from "@/SafeHydrate";
import { Check, Star, Wrench, Calendar, Sparkles, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const PLANS = [{
  Icon: Wrench,
  tier: "Plan 01",
  name: "One-Off Service",
  tag: "Try it once",
  bestFor: "Homes due for a check-up after years of neglect — or a pre-sale tune-up before listing.",
  features: ["Full 12-point inspection", "Spring tension re-balance", "Track alignment &amp; lubrication", "Safety reverse test", "Written service report", "1-Year Labour Warranty on parts replaced"],
  cadence: "1 visit",
  cadenceNote: "One-time",
  cta: "Book a one-off",
  featured: false
}, {
  Icon: Star,
  tier: "Plan 02 · Most chosen",
  name: "Annual Maintenance",
  tag: "Our most popular plan",
  bestFor: "Standard suburban Sydney homes — daily school-run, two cars, normal wear.",
  features: ["Everything in Plan 01", "Priority booking window (book first)", "Free remote battery swap on visit", "Free phone advice between visits", "Auto reminder call &amp; SMS", "10% off any urgent callout in the year"],
  cadence: "1 visit / year",
  cadenceNote: "Annually",
  cta: "Start annual plan",
  featured: true
}, {
  Icon: Sparkles,
  tier: "Plan 03",
  name: "Premium Bi-Annual",
  tag: "Coastal &amp; heavy-use homes",
  bestFor: "Eastern suburbs &amp; beachside homes, busy households, doors used 6+ times daily.",
  features: ["Everything in Plan 02", "2 visits per year (autumn + spring)", "Same-day priority callout if needed", "Free callout fee waived", "Salt-air corrosion treatment", "Bottom seal replacement if perished"],
  cadence: "2 visits / year",
  cadenceNote: "Bi-annually",
  cta: "Go premium",
  featured: false
}];
export default function ServicePlans() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              04 · Pick Your Cadence
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            One service.
            <br />
            <span style={{
            color: GOLD_DARK
          }}>
              Three ways to stay ahead.
            </span>
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            Same flawless 12-point service, three commitment levels. Most
            Sydney homes go with Annual — coastal &amp; busy households
            usually need Premium.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {PLANS.map(({
          Icon,
          tier,
          name,
          tag,
          bestFor,
          features,
          cadence,
          cadenceNote,
          cta,
          featured
        }) => <article key={name} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-3px]" style={{
          backgroundColor: featured ? NAVY : "#ffffff",
          border: featured ? `2px solid ${GOLD}` : `1px solid rgba(8,20,56,0.08)`,
          boxShadow: featured ? `0 30px 80px rgba(8,20,56,0.35), 0 0 0 1px ${GOLD}55` : "0 8px 24px rgba(8,20,56,0.06)",
          transform: featured ? "scale(1.02)" : "scale(1)"
        }}>
                {/* Featured top gold ribbon */}
                {featured && <div className="absolute top-0 left-0 right-0 h-1.5" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 50%, ${GOLD} 100%)`
          }} />}

                {/* Featured stamp */}
                {featured && <div className="absolute" style={{
            top: "20px",
            right: "20px",
            padding: "4px 10px",
            borderRadius: "999px",
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: "10px",
            fontWeight: 900,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            boxShadow: `0 6px 18px ${GOLD}66`
          }}>
                    ★ Most chosen
                  </div>}

                <div className="p-7 md:p-8">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md" style={{
                  width: 32,
                  height: 32,
                  backgroundColor: featured ? `${GOLD}22` : `${GOLD}15`,
                  border: `1px solid ${featured ? `${GOLD}55` : `${GOLD}33`}`
                }}>
                        <Icon size={16} color={featured ? GOLD : GOLD_DARK} strokeWidth={2.5} />
                      </span>
                      <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: featured ? GOLD : GOLD_DARK
                }}>
                        {tier}
                      </span>
                    </div>

                    <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.7rem",
                lineHeight: 1.1,
                color: featured ? "#ffffff" : NAVY,
                letterSpacing: "-0.025em",
                marginBottom: "8px"
              }}>
                      {name}
                    </h3>

                    <p style={{
                fontFamily: FONT_BODY,
                fontSize: "14px",
                lineHeight: 1.5,
                color: featured ? "rgba(255,255,255,0.7)" : INK_SOFT,
                fontStyle: "italic"
              }} dangerouslySetInnerHTML={{
                __html: tag
              }} />
                  </div>

                  {/* Cadence visual */}
                  <div className="mb-6 p-4 rounded-xl flex items-center gap-3" style={{
              backgroundColor: featured ? "rgba(255,255,255,0.06)" : CREAM_DEEP,
              border: featured ? `1px solid rgba(245,166,35,0.25)` : `1px solid rgba(8,20,56,0.06)`
            }}>
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
                width: 40,
                height: 40,
                backgroundColor: featured ? `${GOLD}22` : `${GOLD}18`
              }}>
                      <SafeHydrate><Calendar size={18} color={featured ? GOLD : GOLD_DARK} strokeWidth={2.5} /></SafeHydrate>
                    </span>
                    <div>
                      <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "16px",
                  fontWeight: 800,
                  color: featured ? "#ffffff" : NAVY,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2
                }}>
                        {cadence}
                      </div>
                      <div style={{
                  fontFamily: FONT_BODY,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: featured ? GOLD : GOLD_DARK,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em"
                }}>
                        {cadenceNote}
                      </div>
                    </div>
                  </div>

                  {/* Best for */}
                  <div className="mb-6">
                    <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: featured ? "rgba(255,255,255,0.5)" : INK_SOFT,
                marginBottom: "6px"
              }}>
                      Best for
                    </div>
                    <p style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                lineHeight: 1.55,
                color: featured ? "rgba(255,255,255,0.78)" : INK
              }}>
                      {bestFor}
                    </p>
                  </div>

                  {/* Features list */}
                  <div className="mb-7">
                    <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: featured ? "rgba(255,255,255,0.5)" : INK_SOFT,
                marginBottom: "12px"
              }}>
                      What's included
                    </div>
                    <ul className="space-y-2.5">
                      {features.map(f => <li key={f} className="flex items-start gap-2.5" style={{
                  fontFamily: FONT_BODY,
                  fontSize: "13.5px",
                  lineHeight: 1.5,
                  color: featured ? "rgba(255,255,255,0.88)" : INK,
                  fontWeight: 500
                }}>
                          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex flex-shrink-0 mt-0.5">
                            <Check size={14} color={featured ? GOLD : GOLD_DARK} strokeWidth={3} />
                          </span>
                          <span dangerouslySetInnerHTML={{
                    __html: f
                  }} />
                        </li>)}
                    </ul>
                  </div>

                  {/* CTA */}
                  <Link href="#contact-us" className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full transition-all hover:scale-[1.01]" style={{
              backgroundColor: featured ? GOLD : NAVY,
              color: featured ? NAVY : "#ffffff",
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14px",
              boxShadow: featured ? `0 8px 24px ${GOLD}55` : `0 6px 18px ${NAVY}33`
            }}>
                    {cta}
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <ArrowRight size={14} strokeWidth={2.5} />
                    </span>
                  </Link>
                </div>
              </article>)}
        </div>

        {/* Bottom reassurance strip */}
        <div className="mt-12 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div className="flex items-start gap-4">
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
            width: 44,
            height: 44,
            backgroundColor: `${GOLD}22`,
            border: `1px solid ${GOLD}44`
          }}>
              <ShieldCheck size={20} color={GOLD_DARK} strokeWidth={2.5} />
            </span>
            <div>
              <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1rem",
              color: NAVY,
              marginBottom: "4px",
              letterSpacing: "-0.01em"
            }}>
                Final price quoted on the phone — never on the page.
              </h4>
              <p style={{
              fontFamily: FONT_BODY,
              fontSize: "13.5px",
              color: INK_SOFT,
              lineHeight: 1.55,
              maxWidth: "560px"
            }}>
                Service cost depends on door type, brand, and how long it's
                been since the last tune-up. Tell us yours over a 90-second
                call and we'll lock the price before we leave the yard.
              </p>
            </div>
          </div>

          <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "13px",
          boxShadow: `0 6px 18px ${NAVY}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            Get my flat price
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={13} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>
    </section>;
}